export type PortalPayment = {
  id: string;
  amount: number;
  method: string | null;
  payment_date: string | null;
  reference: string | null;
  status: string;
};

interface Props {
  payments: PortalPayment[];
  brandColor?: string;
}

const CONFIRMED = ["confirmed", "paid", "completed"];
const ACTIVE_STATUSES = ["confirmed", "paid", "completed", "pending"];

const METHOD_ICONS: Record<string, string> = {
  cash: "ri-money-dollar-circle-line",
  card: "ri-bank-card-line",
  zelle: "ri-exchange-dollar-line",
  venmo: "ri-smartphone-line",
  transfer: "ri-bank-line",
  check: "ri-file-list-3-line",
  other: "ri-more-2-line",
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  confirmed:  { label: "Confirmed",  color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
  paid:       { label: "Paid",       color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
  completed:  { label: "Completed",  color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
  pending:    { label: "Pending",    color: "text-amber-700",   bg: "bg-amber-50 border-amber-200" },
  refunded:   { label: "Refunded",   color: "text-violet-700",  bg: "bg-violet-50 border-violet-200" },
  void:       { label: "Void",       color: "text-slate-500",   bg: "bg-slate-50 border-slate-200" },
  cancelled:  { label: "Cancelled",  color: "text-red-600",     bg: "bg-red-50 border-red-200" },
};

function fmtDate(d: string | null) {
  if (!d) return "—";
  try {
    const parsed = new Date(d + (d.includes("T") ? "" : "T00:00:00"));
    if (isNaN(parsed.getTime())) return d;
    return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return d;
  }
}

export default function PaymentsSection({ payments, brandColor = "#C8A96E" }: Props) {
  // Only show non-voided, non-cancelled payments in portal
  const visiblePayments = payments.filter((p) => ACTIVE_STATUSES.includes(p.status));
  const confirmed = payments.filter((p) => CONFIRMED.includes(p.status));
  const pending = payments.filter((p) => p.status === "pending");
  const totalPaid = confirmed.reduce((a, p) => a + Number(p.amount), 0);
  const totalPending = pending.reduce((a, p) => a + Number(p.amount), 0);

  if (visiblePayments.length === 0) return null;

  return (
    <section>
      <h2 className="text-[#0f172a] font-bold text-base mb-4 flex items-center gap-2">
        <span className="w-5 h-5 flex items-center justify-center" style={{ color: brandColor }}>
          <i className="ri-money-dollar-circle-line"></i>
        </span>
        Payment History
      </h2>

      {/* Summary chips */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        {confirmed.length > 0 && (
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <i className="ri-checkbox-circle-line text-emerald-500 text-sm"></i>
            <p className="text-emerald-700 text-sm font-semibold">
              ${totalPaid.toLocaleString()} paid
            </p>
            <span className="text-emerald-400 text-xs">
              · {confirmed.length} payment{confirmed.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
        {pending.length > 0 && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <i className="ri-time-line text-amber-500 text-sm"></i>
            <p className="text-amber-700 text-sm font-semibold">
              ${totalPending.toLocaleString()} pending
            </p>
          </div>
        )}
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="divide-y divide-slate-100">
          {visiblePayments.map((p) => {
            const cfg = STATUS_CONFIG[p.status] ?? STATUS_CONFIG.pending;
            const icon = METHOD_ICONS[p.method ?? ""] ?? "ri-money-dollar-circle-line";
            const isConfirmed = CONFIRMED.includes(p.status);

            return (
              <div key={p.id} className="px-4 py-3.5 flex items-center gap-4">
                {/* Method icon */}
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-50 flex-shrink-0">
                  <i className={`${icon} text-sm`} style={{ color: brandColor }}></i>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-[#0f172a] text-sm font-medium capitalize">
                    {p.method ? `${p.method.charAt(0).toUpperCase() + p.method.slice(1)} payment` : "Payment"}
                  </p>
                  <p className="text-slate-400 text-xs mt-0.5 truncate">
                    {fmtDate(p.payment_date)}
                    {p.reference ? ` · Ref: ${p.reference}` : ""}
                  </p>
                </div>

                {/* Status + Amount */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color}`}>
                    {cfg.label}
                  </span>
                  <p className={`font-bold text-sm ${isConfirmed ? "text-[#0f172a]" : "text-slate-400"}`}>
                    ${Number(p.amount).toLocaleString()}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}